// @flow
import * as React from 'react'
import type { Question } from 'types/question'
import { hasText } from 'utils/helpers'

import HandoutQuestionRenderer from '../../../ui/HandoutQuestionRenderer.jsx'
import ZentakeFormContext from '../ZentakeFormContext'

function HandoutQuestion({
  question,
  isOfficeUse,
}: {
  question: Question,
  isOfficeUse: boolean,
}): React.Node {
  const formContext = React.useContext(ZentakeFormContext)
  const [hasError, setHasError] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState(
    'This field is required'
  )
  const isDirty = formContext?.isQuestionDirty?.(question)
  const isValid = formContext?.isQuestionValid?.(question)
  const questionError = formContext?.getQuestionError?.(question)
  const answer = formContext?.getAnswer?.(question)

  React.useEffect(() => {
    // required means the user must download the handout
    // readonly means the user can only view the handout
    if (question?.is_required) {
      if (answer?.answer_raw !== 'downloaded') {
        setHasError?.(isDirty)
        setErrorMessage('In order to proceed, you must download the handout')
      } else {
        setHasError(false)
        setErrorMessage('')
      }
    } else if (question?.is_read_only) {
      if (!answer?.answer_raw) {
        setHasError?.(isDirty)
        setErrorMessage('In order to proceed, you must view the handout')
      } else {
        setHasError(false)
        setErrorMessage('')
      }
    } else {
      setHasError?.(hasText(questionError))
      setErrorMessage(
        questionError && typeof questionError === 'string'
          ? questionError
          : 'This field is required'
      )
    }
  }, [
    isDirty,
    question?.is_required,
    isValid,
    questionError,
    question?.is_read_only,
    answer,
  ])

  const onAnswerChange = React.useCallback(
    (value: 'viewed' | 'downloaded') => {
      formContext?.onAnswerChanged?.(question, value)
    },
    [formContext, question]
  )

  if (!question?.image) {
    return null
  }

  return (
    <HandoutQuestionRenderer
      errorMessage={errorMessage}
      file={question?.image}
      hasError={hasError}
      helpText={question.help_text}
      isOfficeUse={isOfficeUse}
      isReadOnly={formContext.isQuestionReadonly(question)}
      isRequired={question.is_required}
      label={question.text}
      onDownloaded={() => {
        if (answer !== 'downloaded') {
          onAnswerChange('downloaded')
        }
      }}
      onViewed={() => {
        if (!answer) {
          onAnswerChange('viewed')
        }
      }}
    />
  )
}

export default HandoutQuestion
